"use client";

import Link from "next/link";
import React, { useEffect, useMemo, useState } from "react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { buildTermsItemPolicies } from "@/lib/termsItemPolicies";

export default function Terms() {
  const [expandedSections, setExpandedSections] = useState<number[]>([0]); // Start with first section expanded
  const [activeCategoryIds, setActiveCategoryIds] = useState<string[] | null>(
    null,
  );

  const toggleSection = (sectionIndex: number) => {
    setExpandedSections((prev) =>
      prev.includes(sectionIndex)
        ? prev.filter((index) => index !== sectionIndex)
        : [...prev, sectionIndex],
    );
  };

  useEffect(() => {
    let isMounted = true;

    const fetchActiveCategories = async () => {
      try {
        const response = await fetch("/api/categories/active", {
          cache: "no-store",
          headers: { "Cache-Control": "no-cache" },
        });

        if (!response.ok) {
          throw new Error(
            `Failed to load active categories: ${response.status}`,
          );
        }

        const data = (await response.json()) as { categories?: string[] };

        if (isMounted) {
          setActiveCategoryIds(
            Array.isArray(data.categories) ? data.categories : [],
          );
        }
      } catch (error) {
        console.error(
          "Unable to load active categories for terms page:",
          error,
        );
        if (isMounted) {
          setActiveCategoryIds(null);
        }
      }
    };

    fetchActiveCategories();

    return () => {
      isMounted = false;
    };
  }, []);

  const itemPolicies = useMemo(
    () => buildTermsItemPolicies(activeCategoryIds ?? undefined),
    [activeCategoryIds],
  );

  const sections = [
    {
      title: "1. Acceptance of Terms",
      content: (
        <p className="text-text-light">
          By accessing and using Sell It Off, you agree to be bound by these
          Terms of Service and all applicable laws and regulations. If you do
          not agree with any of these terms, you are prohibited from using or
          accessing this site. Sell It Off is a platform designed for buying and
          selling pre-owned household items starting with Lagos, Nigeria (we
          will expand to other cities in Nigeria and Africa at large in the
          future).
        </p>
      ),
    },
    {
      title: "2. User Accounts",
      content: (
        <>
          <p className="text-text-light">
            To use certain features of the site, you must register for an
            account. You agree to:
          </p>
          <ul className="list-disc pl-6 text-text-light mt-4">
            <li>
              Provide accurate and complete information, including a valid
              contact number and location
            </li>
            <li>Maintain the security of your account</li>
            <li>Accept responsibility for all activities under your account</li>
            <li>Not share your account credentials</li>
            <li>Keep your location and contact information updated</li>
            <li>Respond to inquiries within a reasonable timeframe</li>
          </ul>
        </>
      ),
    },
    {
      title: "3. Listing and Selling",
      content: (
        <>
          <p className="text-text-light">
            When listing items for sale, you agree to:
          </p>
          <ul className="list-disc pl-6 text-text-light mt-4">
            <li>
              Provide accurate descriptions, condition details, and clear images
              (exactly 3 images per item)
            </li>
            <li>List only items you legally own and have the right to sell</li>
            <li>Honestly disclose any defects or issues with the item</li>
            <li>Set reasonable prices between ₦20,000 and ₦3,000,000</li>
            <li>Specify accurate pickup availability days and times</li>
            <li>Comply with all applicable laws and regulations</li>
            <li>
              Maintain accurate inventory and update item status if sold
              elsewhere
            </li>
            <li>Allow potential buyers to inspect the item before purchase</li>
            <li>
              Accept that once your item is approved, price adjustments can only
              be made downwards
            </li>
            <li>
              List only neatly used household items that meet our quality
              standards
            </li>
            <li>
              Understand that payment will be processed once the buyer picks up
              the item from you
            </li>
          </ul>

          <p className="text-text-light mt-6 font-bold">
            Listing quality requirements:
          </p>
          <p className="text-text-light mt-2">
            All listings are reviewed by our admin team before going live.
            Approval is at Sell It Off's discretion. To be approved, a listing
            must meet the following standards:
          </p>
          <ul className="list-disc pl-6 text-text-light mt-4">
            <li>
              Photos must be of the actual item being sold — no stock images,
              screenshots, or photos taken from other websites
            </li>
            <li>
              Photos must be clear, well-lit, and show the item's true condition
              from multiple angles
            </li>
            <li>
              The title and description must accurately match the photos and the
              item being sold
            </li>
            <li>
              The price must be realistic for the item's category, condition,
              and age
            </li>
            <li>
              The pickup location must be accurate and reflect where the item
              can actually be collected
            </li>
            <li>
              The item must fall within an accepted category (see Section 4) and
              not be a prohibited item
            </li>
          </ul>
          <p className="text-text-light mt-4">
            Listings that do not meet these standards may be rejected with a
            reason and may be resubmitted after the issue is corrected. Edits to
            an approved listing will return it to review and trigger a 24-hour
            cooldown before further edits can be made.
          </p>
        </>
      ),
    },
    {
      title: "4. Accepted & Prohibited Items",
      content: (
        <>
          <p className="text-text-light font-bold">
            The following items are accepted for listing on Sell It Off:
          </p>
          <ul className="list-disc pl-6 text-text-light mt-4">
            {itemPolicies.acceptedItems.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <p className="text-text-light text-sm mt-3">
            This list is generated from current category activation settings and
            updates as categories are enabled or disabled.
          </p>

          <p className="text-text-light mt-6 font-bold">
            The following items are prohibited from being listed:
          </p>
          <ul className="list-disc pl-6 text-text-light mt-4">
            {itemPolicies.prohibitedItems.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </>
      ),
    },
    {
      title: "5. Buying Process",
      content: (
        <>
          <p className="text-text-light">
            When buying items on Sell It Off, you agree to:
          </p>
          <ul className="list-disc pl-6 text-text-light mt-4">
            <li>
              Review item details carefully, including location and proximity,
              before making payment
            </li>
            <li>Understand that we do not support "payment on delivery"</li>
            <li>
              Schedule inspection and pickup within 48 hours of receiving seller
              details
            </li>
            <li>
              Understand that if you do not schedule pickup or communicate
              promptly within that 48-hour window, the sale may be released and
              the item may be unlocked for other buyers, and any refund may be
              subject to the administrative fee described in Section 6 and the
              Refund Policy
            </li>
            <li>Personally inspect items before confirming receipt</li>
            <li>
              Acknowledge that no refunds will be issued after pickup and
              confirmation
            </li>
            <li>
              Accept that Sell It Off is not liable for any defects or issues
              identified after pickup
            </li>
            <li>Communicate promptly with sellers about pickup arrangements</li>
            <li>Follow safety guidelines for in-person transactions</li>
            <li>Complete the transaction within the agreed timeframe</li>
          </ul>
        </>
      ),
    },
    {
      title: "6. Refund Policy",
      content: (
        <>
          <p className="text-text-light">
            The full, customer-readable policy is published on our{" "}
            <Link
              href="/refund-policy"
              className="text-blue-600 hover:underline"
            >
              Refund Policy
            </Link>{" "}
            page. The summary below is part of these Terms.
          </p>
          <p className="text-text-light mt-4">
            Refunds at Sell It Off are subject to the following conditions:
          </p>
          <ul className="list-disc pl-6 text-text-light mt-4">
            <li>
              <strong>Full Refunds (100%)</strong> are only applicable under
              these circumstances at the pickup location, before the buyer
              confirms receipt:
              <ul className="list-disc pl-6 mt-2">
                <li>
                  There is a clear mismatch between the advertised description
                  and the actual condition of the item
                </li>
                <li>
                  The item is no longer available or the seller changes their
                  mind (does not apply if the seller's change of mind is due to
                  delays or lack of communication from the buyer after payment)
                </li>
                <li>
                  The item has a significant defect that was not previously
                  disclosed in the listing
                </li>
              </ul>
            </li>
            <br />
            <li>
              <strong>Refund restrictions and buyer delay:</strong>
              <ul className="list-disc pl-6 mt-2">
                <li>
                  Once an item has been picked up and marked as delivered, and
                  the seller has been paid, no refund will be issued
                </li>
                <li>
                  Buyers must schedule inspection and pickup within 48 hours of
                  receiving seller details and stay in prompt contact with the
                  seller. If the buyer delays beyond that window or fails to
                  contact the seller promptly, the reserved sale may be released
                  and the item unlocked so it can be offered to other buyers. If
                  a refund is then due to the buyer, a 5% administrative fee
                  will be deducted from the payment before the remainder is
                  refunded.
                </li>
              </ul>
            </li>
          </ul>
        </>
      ),
    },
    {
      title: "7. Fees and Payments",
      content: (
        <>
          <p className="text-text-light">
            Sell It Off charges a 10% fee on every successful sale. You agree
            to:
          </p>
          <ul className="list-disc pl-6 text-text-light mt-4">
            <li>Pay all applicable fees associated with using the service</li>
            <li>Accept that the 10% platform fee is non-refundable</li>
            <li>
              Complete payment transactions in the manner specified by the
              platform
            </li>
            <li>Not attempt to circumvent the platform's fee structure</li>
            <li>Report any payment issues promptly to customer service</li>
          </ul>
        </>
      ),
    },
    {
      title: "8. Dispute Resolution",
      content: (
        <>
          <p className="text-text-light">
            In the event of a dispute between users, Sell It Off will:
          </p>
          <ul className="list-disc pl-6 text-text-light mt-4">
            <li>Provide a platform for communication between parties</li>
            <li>
              Review evidence from both parties, including messages and
              transaction details
            </li>
            <li>Make a fair determination based on available information</li>
            <li>
              Take appropriate action to resolve the dispute, which may include
              refunds or account restrictions
            </li>
            <li>Reserve the right to make the final decision on any dispute</li>
          </ul>
        </>
      ),
    },
    {
      title: "9. Limitation of Liability",
      content: (
        <>
          <p className="text-text-light">
            At Sell It Off, we do our best to ensure that the platform is safe
            and secure for all users. However, we shall not be liable for:
          </p>
          <ul className="list-disc pl-6 text-text-light mt-4">
            <li>
              Any indirect, incidental, special, consequential, or punitive
              damages
            </li>
            <li>
              Item quality or condition discrepancies not disclosed by the
              seller
            </li>
            <li>
              Personal injury or property damage during item inspection or
              pickup
            </li>
            <li>
              Any issues arising from direct transactions between buyers and
              sellers
            </li>
            <li>
              Loss of opportunity or profit due to platform unavailability
            </li>
            <li>
              Personal safety at meetups between users. Sell It Off does not
              take responsibility for injury, loss, or harm that happens when
              users meet
            </li>
            <li>
              Any defects or issues identified after pickup is completed and
              confirmed
            </li>
          </ul>
        </>
      ),
    },
    {
      title: "10. Logistics and Delivery",
      content: (
        <>
          <p className="text-text-light">
            Please note that currently, Sell It Off does not handle or provide
            delivery services. Regarding logistics and delivery:
          </p>
          <ul className="list-disc pl-6 text-text-light mt-4">
            <li>
              Buyers and sellers are responsible for arranging their own pickup
              and delivery logistics
            </li>
            <li>
              All transactions are conducted on an in-person basis at the agreed
              location
            </li>
            <li>
              Sell It Off is not responsible for any logistics costs or issues
              that arise during pickup or delivery
            </li>
            <li>
              Users are advised to communicate clearly about pickup times and
              locations
            </li>
            <li>
              Users must adhere to the pickup availability times specified in
              the listing unless otherwise agreed
            </li>
            <li>
              For now, the platform only facilitates the connection between
              buyers and sellers
            </li>
            <li>
              Users are encouraged to meet in safe, public locations for
              transactions
            </li>
            <li>Inspect items thoroughly before completing the transaction</li>
          </ul>
        </>
      ),
    },
    {
      title: "11. Safety Guidelines",
      content: (
        <>
          <p className="text-text-light">
            For your safety when using Sell It Off:
          </p>
          <ul className="list-disc pl-6 text-text-light mt-4">
            <li>
              Always meet in public, well-lit places for item inspection and
              exchange
            </li>
            <li>Inform a friend or family member about your meeting details</li>
            <li>
              Verify the identity of the other party through the platform before
              meeting
            </li>
            <li>Inspect items thoroughly before completing the transaction</li>
            <li>
              Deal with users through the provisions of the platform, they have
              been created with your best interest in mind
            </li>
            <li>
              Report suspicious behavior to Sell It Off{" "}
              <strong>immediately</strong>
            </li>
            <li>
              For high-value items, consider meeting at a secure location like a
              bank or police station
            </li>
          </ul>
          <p className="text-text-light mt-4">
            Sell It Off does not take responsibility for your personal safety
            when you meet another user. You choose where and how you meet, and
            you attend at your own risk. We are not liable for injury, loss, or
            harm that happens at a meetup.
          </p>
        </>
      ),
    },
    {
      title: "12. User Content and Reviews",
      content: (
        <>
          <p className="text-text-light">
            Regarding content you post on Sell It Off:
          </p>
          <ul className="list-disc pl-6 text-text-light mt-4">
            <li>
              You retain ownership of your content but grant Sell It Off a
              license to use it
            </li>
            {/* <li>Reviews and ratings should be honest and based on actual experiences</li> */}
            <li>Harassment, hate speech, or false information is prohibited</li>
            <li>
              Sell It Off reserves the right to remove any content violating
              these terms
            </li>
            <li>
              Images must be of the actual item being sold, not stock photos
            </li>
          </ul>
        </>
      ),
    },
    {
      title: "13. Changes to Terms",
      content: (
        <p className="text-text-light">
          We reserve the right to modify these terms at any time. We will notify
          our users of any material changes via email or through the platform.
          Continued use of the platform after changes constitutes acceptance of
          the updated terms.
        </p>
      ),
    },
  ];

  return (
    <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
      <h1 className="text-4xl font-bold text-foreground mb-8">
        Terms of Service
      </h1>

      <div className="prose prose-lg">
        <p className="text-text-light mb-8">
          Last updated: {new Date().toLocaleDateString()}
        </p>

        <div className="space-y-4">
          {sections.map((section, index) => (
            <div key={index} className="border rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection(index)}
                className="w-full flex justify-between items-center p-4 bg-secondary hover:bg-tertiary transition-colors text-left"
              >
                <h2 className="text-xl font-semibold text-foreground">
                  {section.title}
                </h2>
                {expandedSections.includes(index) ? (
                  <ChevronUpIcon className="h-6 w-6 text-text-light" />
                ) : (
                  <ChevronDownIcon className="h-6 w-6 text-text-light" />
                )}
              </button>

              {expandedSections.includes(index) && (
                <div className="p-4">{section.content}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
